import { useMutation } from '@tanstack/react-query'

import { GET_PROJECTS_QUERY } from '@/hooks/queries/use-get-projects'
import { useAuth } from '@/hooks/use-auth'
import { DefaultProjectFormData } from '@/pages/projects-form/contexts/project-context'
import { createRegions, Region } from '@/services/create-regions'
import { editProject, EditRegionsPayload } from '@/services/edit-project'
import { queryClient } from '@/services/query-client'

export const useEditProject = (id?: number) => {
  const { user } = useAuth()

  const mutation = useMutation(
    async (payload: DefaultProjectFormData) => {
      const filteredRegions = payload.regions.reduce(
        (acc, region) => {
          if (!region.id) {
            acc.insertRegions.push(region.name)
          } else if (region.removed) {
            acc.deactivateRegions.push(region.id)
          }

          return acc
        },
        {
          insertRegions: [],
          deactivateRegions: [],
        } as EditRegionsPayload,
      )

      const { regions } = await editProject(id!, {
        name: payload.project_name,
        owner: user?.name,
        address: {
          city: payload.city,
          state: payload.state.name,
        },
        regions: filteredRegions,
      })

      const regionsPayload = payload.regions
        .filter((region) => !region.removed)
        .map((region) => {
          const auxRegion = { ...region, is_new_region: false }

          if (!region.id) {
            auxRegion.id = regions.find((r) => r.name === region.name)?.id
            auxRegion.is_new_region = true
          }

          return auxRegion
        })
        .filter((region) => {
          const isNewRegion = region.is_new_region && region.geojson
          const isModifiedRegion =
            region.geojson || region.removed_features.length

          return isNewRegion || isModifiedRegion
        })
        .map<Region>((region) => {
          return {
            project_id: id!,
            region_id: region.id!,
            geojson: region.geojson,
            col_name: region.col_name!,
            removed_features: region.removed_features.map(
              (feature) => feature.id,
            ),
            region_name: region.name,
          }
        })

      if (regionsPayload.length) {
        await createRegions(regionsPayload)
      }

      return { id }
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([GET_PROJECTS_QUERY])
      },
    },
  )

  return mutation
}
